/**
 * Withdrawals & Payouts Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const WITHDRAWALS_ARTICLES_RU: HelpArticle[] = [
  {
    id: "wd-001",
    slug: "payout-process-overview",
    title: "Как работают выплаты",
    description: "Как выводить средства на банковский счёт",
    category: "withdrawals",
    popular: true,
    order: 1,
    keywords: ["выплата", "вывод", "банк", "процесс", "перевод"],
    content: `
# Как работают выплаты

После конвертации крипты — вот как вы получаете фиат на банковский счёт.

## Процесс выплаты

1. **Конвертация завершена** — Крипта конвертирована в фиат
2. **Баланс доступен** — Средства отображаются на счёте
3. **Выплата запущена** — Автоматически или вручную
4. **Банковский перевод** — Отправлен выбранным способом
5. **Средства получены** — Деньги на вашем банковском счёте

## Автоматические и ручные выплаты

### Автоматические выплаты
- Средства отправляются сразу после конвертации
- Действий от вас не требуется
- Настройте порог в настройках
- Подходит для регулярного использования

### Ручные выплаты
- Запросите вывод, когда удобно
- Контролируйте время
- Объединяйте несколько депозитов
- Подходит для крупных сумм

## Способы выплаты

| Способ | Регионы | Скорость |
|--------|---------|----------|
| ACH | США | 2–3 рабочих дня |
| Wire | США, международный | 1–2 рабочих дня |
| SEPA | Европа | 1 рабочий день |
| SPEI | Мексика | В тот же день |

## Настройка выплат

1. Пройдите KYC-верификацию
2. Добавьте реквизиты банковского счёта
3. Подтвердите владение счётом
4. Настройте параметры выплат
5. Начните получать средства

## Лимиты выплат

| Уровень | В день | В месяц |
|---------|--------|---------|
| Стандартный | $25 000 | $100 000 |
| Расширенный | $100 000 | $500 000 |
| Корпоративный | Индивидуально | Индивидуально |
    `.trim(),
  },
  {
    id: "wd-002",
    slug: "adding-bank-accounts",
    title: "Добавление банковских счетов",
    description: "Как привязать банковский счёт для выплат",
    category: "withdrawals",
    popular: true,
    order: 2,
    keywords: ["банк", "счёт", "добавить", "привязать", "настройка"],
    content: `
# Добавление банковских счетов

Привяжите банковский счёт для получения выплат в фиате.

## Требования

- Пройденная KYC-верификация
- Банковский счёт на ваше имя
- Счёт принимает входящие переводы

## Добавление счёта

### Шаг 1: Перейдите в настройки
1. Откройте Настройки > Выплаты
2. Нажмите «Добавить банковский счёт»
3. Выберите страну

### Шаг 2: Введите реквизиты

**Для счетов в США (ACH)**
- Routing number (9 цифр)
- Номер счёта
- Тип счёта (Checking/Savings)
- Имя владельца счёта

**Для счетов в США (Wire)**
- То же, что ACH, плюс
- Название банка
- Адрес банка

**Для европейских счетов (SEPA)**
- IBAN
- BIC/SWIFT (опционально)
- Имя владельца счёта

**Для счетов в Мексике (SPEI)**
- Номер CLABE (18 цифр)
- Название банка
- Имя владельца счёта

### Шаг 3: Подтвердите счёт
- Верификация микродепозитами (ACH)
- Или верификация документами
- Обычно 1–2 рабочих дня

## Верификация счёта

### Микродепозиты (ACH)
1. Мы отправляем два небольших перевода
2. Проверьте выписку
3. Введите суммы в Supabank
4. Счёт подтверждён!

### Верификация документами
1. Загрузите банковскую выписку
2. Должно быть видно ваше имя
3. Должен быть виден номер счёта
4. Проверка в течение 24 часов

## Несколько счетов

- Можно добавить до 5 счетов
- Установите основной для выплат
- Переключайтесь между счетами
- Удаляйте ненужные

## Советы

**Правильно:**
- Используйте счета на ваше юридическое имя
- Перепроверяйте все номера
- Используйте счета, к которым есть регулярный доступ

**Неправильно:**
- Использовать чужие счета
    `.trim(),
  },
  {
    id: "wd-003",
    slug: "supported-payout-currencies",
    title: "Валюты для выплат",
    description: "Доступные валюты для банковских выплат",
    category: "withdrawals",
    order: 3,
    keywords: ["валюта", "фиат", "usd", "eur", "mxn"],
    content: `
# Валюты для выплат

Получайте выплаты в удобной фиатной валюте.

## Доступные валюты

| Валюта | Символ | Регионы |
|--------|--------|---------|
| Доллар США | USD | США, международный |
| Евро | EUR | Европа, международный |
| Мексиканский песо | MXN | Мексика |

## Выбор валюты

### При конвертации
- Крипта конвертируется в выбранный фиат
- Установите по умолчанию в настройках
- Выбор для каждой транзакции (скоро)

### Валюта выплаты
- Должна совпадать с валютой счёта
- Или применяется конвертация

## Обменные курсы

- Рыночные курсы в реальном времени
- Конкурентные спреды
- Прозрачное ценообразование
- Без скрытых наценок

## Выплаты в USD

**ACH (Automated Clearing House)**
- Для счетов в банках США
- 2–3 рабочих дня
- Низкие комиссии

**Wire-перевод**
- Внутренний и международный
- 1–2 рабочих дня
- Выше комиссии, быстрее

## Выплаты в EUR

**SEPA (Single Euro Payments Area)**
- Для европейских счетов
- 1 рабочий день
- Низкие комиссии

**Международный wire**
- Страны вне SEPA
- 2–3 рабочих дня
- Стандартные комиссии wire

## Выплаты в MXN

**SPEI (Sistema de Pagos Electrónicos Interbancarios)**
- Для счетов в Мексике
- В тот же день (в рабочие часы)
- Очень низкие комиссии
    `.trim(),
  },
  {
    id: "wd-004",
    slug: "wire-vs-ach-vs-sepa",
    title: "Wire, ACH и SEPA",
    description: "Различия между платёжными системами",
    category: "withdrawals",
    order: 4,
    keywords: ["wire", "ach", "sepa", "перевод", "платёж"],
    content: `
# Wire, ACH и SEPA

Понимание разных способов получения выплат.

## Краткое сравнение

| Характеристика | ACH | Wire | SEPA |
|----------------|-----|------|------|
| Регион | США | Весь мир | Европа |
| Скорость | 2–3 дня | 1–2 дня | 1 день |
| Комиссия | $0 | $15–25 | €0–3 |
| Лимит | $25 000 | $100 000+ | €100 000 |
| Для кого | Регулярные выплаты | Крупные/срочные | Европейские счета |

## ACH (Automated Clearing House)

**Что это:**
Внутренняя платёжная сеть США

**Плюсы:**
- Бесплатно или очень дёшево
- Надёжная и проверенная система
- Подходит для регулярных платежей

**Минусы:**
- Занимает 2–3 рабочих дня
- Только счета в США
- Ниже лимиты на транзакцию

**Для кого:**
- Регулярные, несрочные выплаты
- Экономные пользователи
- Владельцы счетов в США

## Wire-перевод

**Что это:**
Электронный перевод между банками

**Плюсы:**
- Самый быстрый вариант
- Высокие лимиты
- Работает международно

**Минусы:**
- Выше комиссии ($15–25)
- Банк-получатель тоже может брать комиссию
- Избыточен для мелких сумм

**Для кого:**
- Крупные выплаты
- Срочные переводы
- Международные счета

## SEPA (Single Euro Payments Area)

**Что это:**
Европейская платёжная интеграция

**Плюсы:**
- Быстро (часто в тот же день)
- Низкие комиссии (€0–3)
- 36 европейских стран

**Минусы:**
- Только EUR
- Только Европа
- Есть ограничения

**Для кого:**
- Европейские пользователи
- Выплаты в EUR
- Регулярные переводы

## Как выбрать способ

### Для пользователей из США
- < $5 000: ACH
- > $5 000 или срочно: Wire

### Для европейских пользователей
- SEPA для всего в EUR
- Wire для других валют

### Для пользователей из Мексики
- SPEI для всех выплат в MXN
    `.trim(),
  },
  {
    id: "wd-005",
    slug: "payout-processing-times",
    title: "Сроки обработки выплат",
    description: "Сколько времени идут средства в банк",
    category: "withdrawals",
    order: 5,
    keywords: ["обработка", "время", "ожидание", "скорость"],
    content: `
# Сроки обработки выплат

Узнайте, когда ожидать средства на банковском счёте.

## Этапы обработки

### 1. Инициация (мгновенно)
- Запрос на выплату отправлен
- Проверки комплаенса
- Статус: «Обработка»

### 2. Отправка в банк (0–4 часа)
- Передано платёжному процессору
- Банк получил инструкцию
- Статус: «Отправлено»

### 3. Обработка банком (зависит от способа)
- Банк обрабатывает перевод
- Зависит от способа оплаты
- Статус: «В пути»

### 4. Зачисление (завершено)
- Средства на счёте
- Статус: «Завершено»

## Ожидаемые сроки

| Способ | Обработка Supabank | Обработка банка | Итого |
|--------|-------------------:|----------------:|------:|
| ACH | В тот же день | 2–3 дня | 2–3 дня |
| Wire | В тот же день | 1–2 дня | 1–2 дня |
| SEPA | В тот же день | 1 день | 1 день |
| SPEI | В тот же день | В тот же день | В тот же день |

## Рабочие дни

- Обработка с понедельника по пятницу
- Исключая банковские праздники
- Действуют времена отсечки
- Запросы в выходные ставятся в очередь на понедельник

## Времена отсечки

| Способ | Отсечка (ET) | Обработка в тот же день |
|--------|--------------|-------------------------|
| ACH | 16:00 | Если до отсечки |
| Wire | 16:00 | Если до отсечки |
| SEPA | 14:00 CET | Если до отсечки |
| SPEI | 17:00 CT | Если до отсечки |

## Задержки

Выплаты могут задерживаться из-за:
- Первой крупной суммы
- Нового банковского счёта
- Дополнительной проверки
- Банковских праздников

## Отслеживание выплаты

1. Откройте «Транзакции»
2. Найдите выплату
3. Посмотрите подробный статус
4. Смотрите ориентировочное время прибытия
    `.trim(),
  },
  {
    id: "wd-006",
    slug: "failed-payouts-troubleshooting",
    title: "Проблемы с выплатами",
    description: "Что делать, если выплата не прошла",
    category: "withdrawals",
    order: 6,
    keywords: ["ошибка", "отклонено", "возврат", "проблема"],
    content: `
# Проблемы с выплатами

Иногда выплаты не завершаются успешно. Вот как решить проблемы.

## Частые причины отказа

### 1. Неверные реквизиты
- Неправильный номер счёта
- Неверный routing number
- Ошибки в IBAN

**Решение:** Проверьте и введите реквизиты заново

### 2. Несовпадение имени
- Имя не совпадает с профилем Supabank
- Проблемы с совместным счётом
- Бизнес vs личный счёт

**Решение:** Используйте счёт на ваше верифицированное имя

### 3. Отказ банка
- Счёт закрыт
- Счёт заморожен
- Банк не принимает переводы

**Решение:** Свяжитесь с банком или используйте другой счёт

### 4. Превышен лимит
- Достигнут дневной/месячный лимит
- Слишком большая сумма

**Решение:** Дождитесь сброса лимита или разбейте выплату

## Что происходит с отклонёнными выплатами

1. **Уведомление о возврате** — Мы получаем отказ
2. **Возврат средств** — Деньги возвращаются на баланс
3. **Уведомление** — Вы получаете email
4. **Готово к повтору** — Можно попробовать снова

## Сроки возврата

| Способ | Время возврата |
|--------|----------------|
| ACH | 2–3 рабочих дня |
| Wire | 1–2 рабочих дня |
| SEPA | 1 рабочий день |

## Решение проблем

### Шаг 1: Проверьте реквизиты
1. Откройте Настройки > Выплаты
2. Проверьте реквизиты счёта
3. Сравните с банковской выпиской
4. Исправьте ошибки

### Шаг 2: Свяжитесь с банком
- Убедитесь, что счёт принимает переводы
- Проверьте ограничения
- Подтвердите, что счёт активен

### Шаг 3: Повторите выплату
1. Убедитесь в правильности данных
2. Запросите новую выплату
3. Следите за статусом

### Шаг 4: Обратитесь в поддержку
Если проблемы повторяются:
- Напишите на support@supabank.xyz
- Укажите: ID транзакции, сообщение об ошибке
- Мы свяжемся с платёжным процессором
    `.trim(),
  },
  {
    id: "wd-007",
    slug: "payout-fees-explained",
    title: "Комиссии за выплаты",
    description: "Разбор комиссий за вывод средств",
    category: "withdrawals",
    order: 7,
    keywords: ["комиссии", "стоимость", "цена", "вывод"],
    content: `
# Комиссии за выплаты

Прозрачный разбор всех комиссий, связанных с выплатами.

## Структура комиссий

### Комиссии Supabank

| Способ | Наша комиссия |
|--------|---------------|
| ACH | $0 |
| Внутренний Wire | $15 |
| Международный Wire | $25 |
| SEPA | €0 |
| SPEI | $0 |

### Комиссии за конвертацию

При конвертации крипты:
- Комиссия платформы: 2.0% от суммы
- Комиссия Bridge: ~0.1% от суммы
- Итого: ~2.1% от суммы
- Зависит от конвертируемой суммы
- Все комиссии показаны до подтверждения

## Сторонние комиссии

### Ваш банк может взимать

| Тип комиссии | Сумма |
|--------------|-------|
| Входящий wire | $10–25 |
| Международный перевод | $15–50 |
| Конвертация валюты | 1–3% |

*Уточните в вашем банке конкретные комиссии*

### Банки-посредники

Для международных wire:
- Могут удерживать комиссии по пути
- Обычно $10–30
- Комиссии сети SWIFT

## Примеры комиссий

### Пример 1: ACH в США
- Сумма выплаты: $1 000
- Комиссия Supabank: $0
- Комиссия банка: $0 (обычно)
- **Вы получаете: $1 000**

### Пример 2: Международный Wire
- Сумма выплаты: $10 000
- Комиссия Supabank: $25
- Посредники: ~$20
- Ваш банк: ~$15
- **Вы получаете: ~$9 940**

### Пример 3: SEPA
- Сумма выплаты: €5 000
- Комиссия Supabank: €0
- Ваш банк: €0–3
- **Вы получаете: €4 997–5 000**

## Как снизить комиссии

**Лучшие практики:**
- Используйте ACH для США (бесплатно)
- Используйте SEPA для Европы (бесплатно)
- Объединяйте мелкие выплаты
- Выбирайте банки с низкими комиссиями за входящие
    `.trim(),
  },
  {
    id: "wd-008",
    slug: "withdrawal-limits",
    title: "Лимиты на вывод",
    description: "Какие лимиты на выплаты",
    category: "withdrawals",
    order: 8,
    keywords: ["лимиты", "максимум", "дневной", "месячный", "ограничение"],
    content: `
# Лимиты на вывод

Лимиты зависят от уровня верификации и истории аккаунта.

## Уровни лимитов

### Стандартный (по умолчанию)
- За транзакцию: $10 000
- В день: $25 000
- В месяц: $100 000

### Расширенный
- За транзакцию: $50 000
- В день: $100 000
- В месяц: $500 000

### Корпоративный
- За транзакцию: Индивидуально
- В день: Индивидуально
- В месяц: Индивидуально

## Как считаются лимиты

### Дневные лимиты
- Сбрасываются в полночь UTC
- Включают все выплаты
- По всем банковским счетам

### Месячные лимиты
- Календарный месяц
- Сбрасываются 1-го числа
- Суммарно по всем способам

## Проверка лимитов

1. Откройте Настройки > Лимиты
2. Посмотрите текущий уровень
3. Проверьте оставшиеся лимиты
4. Отслеживайте использование

## Повышение лимитов

### Стандартный → Расширенный
Требования:
- 30 дней с момента регистрации
- Успешные транзакции
- Дополнительная верификация:
  - Подтверждение адреса
  - Источник средств

### Расширенный → Корпоративный
Требования:
- Бизнес-аккаунт
- История больших объёмов
- Индивидуальная проверка
- Свяжитесь с отделом продаж

## Временные ограничения

Временное снижение лимитов возможно при:
- Новых аккаунтах
- Необычной активности
- Ожидающей верификации
- Вопросах безопасности

## Советы

**Лучшие практики:**
- Планируйте крупные выводы заранее
- Верифицируйтесь до сброса лимитов
- Обратитесь в поддержку для повышения
- При необходимости разбивайте на несколько транзакций
    `.trim(),
  },
];
